import type { OrderItem } from '../../graphql/query/getOrder';
import type { PackagingOption } from '../../graphql/query/getDelivery';
import type { SubmitStatus } from '../../graphql/mutation/submit';
import type {
  SubmittedItem,
  RequestItemData,
  InstructionContext,
  ClaimStatus,
} from './types';
import { languageData, rawString } from '../Language/Language';

export const getSubmittedItems = ({
  requestItems,
  orderItems,
}: {
  orderItems?: OrderItem[];
  requestItems: RequestItemData[];
}) =>
  requestItems?.reduce<SubmittedItem[]>((acc, item) => {
    // Try to find matching order item for additional details
    const orderItem = orderItems?.find((i) => i.id === item.id);

    // Only include items that have matching order data
    // Missing order items indicate a data sync issue and should not be displayed
    // with fake/default values (e.g., $0.00 price) as this misleads users
    if (orderItem) {
      acc.push({
        ...orderItem,
        quantity: item.quantity ?? 1,
      });
    }
    // Skip items without matching order data rather than creating fake items
    return acc;
  }, []) ?? [];

export const getAvailableInstructionCount = ({
  status,
  type,
  packaging,
  claimStatus,
  keyPrefix = 'pdx',
}: {
  type?: string;
  packaging?: PackagingOption | null;
  status: SubmitStatus;
  claimStatus?: ClaimStatus;
  keyPrefix?: string;
}): number => {
  const baseKeys: string[] = getBaseKeys({
    status,
    type,
    packaging,
    claimStatus,
    keyPrefix,
  });
  const languageKeys = Object.keys(languageData());

  // Check each base key to find the highest instruction number
  let maxInstructionNumber = 0;

  for (const baseKey of baseKeys) {
    // Look for title keys in the language data
    const titlePattern = new RegExp(
      `^${baseKey.replace(/\./g, '\\.')}\\.title\\.(\\d+)(?:\\.(singular|plural))?$`,
    );

    for (const langKey of languageKeys) {
      const match = langKey.match(titlePattern);
      if (match && match[1]) {
        const instructionNumber = parseInt(match[1], 10);
        maxInstructionNumber = Math.max(
          maxInstructionNumber,
          instructionNumber,
        );
      }
    }
  }

  return maxInstructionNumber;
};

export const getBaseKeys = ({
  status,
  type,
  packaging,
  claimStatus,
  keyPrefix = 'pdx',
}: {
  type?: string;
  packaging?: PackagingOption | null;
  status: SubmitStatus;
  claimStatus?: ClaimStatus;
  keyPrefix?: string;
}) => {
  const basePrefix = `${keyPrefix}.confirmation`;
  const keys = [`${basePrefix}.instructions`];

  // Convert to lowercase for MX-style keys
  const normalizedType = type?.toLowerCase();
  const normalizedPackaging = packaging?.toLowerCase();
  const normalizedStatus = status?.toLowerCase();
  const normalizedClaimStatus = claimStatus?.toLowerCase();

  if (normalizedType && normalizedPackaging) {
    keys.unshift(
      `${basePrefix}.${normalizedClaimStatus}.instructions.${normalizedType}.${normalizedPackaging}`,
      `${basePrefix}.${normalizedStatus}.instructions.${normalizedType}.${normalizedPackaging}`,
      `${basePrefix}.instructions.${normalizedType}.${normalizedPackaging}`,
    );
  } else if (normalizedType) {
    keys.unshift(
      `${basePrefix}.${normalizedClaimStatus}.instructions.${normalizedType}`,
      `${basePrefix}.${normalizedStatus}.instructions.${normalizedType}`,
      `${basePrefix}.instructions.${normalizedType}`,
    );
  } else {
    keys.unshift(
      `${basePrefix}.${normalizedClaimStatus}.instructions`,
      `${basePrefix}.${normalizedStatus}.instructions`,
    );
  }

  return keys;
};

// Used to render the secondary email or item preview widget above instructions
export const getContextWidget = ({
  status,
  type,
  packaging,
  claimStatus,
  keyPrefix = 'pdx',
}: {
  type?: string;
  packaging?: PackagingOption | null;
  status: SubmitStatus;
  claimStatus?: ClaimStatus;
  keyPrefix?: string;
}): InstructionContext | null => {
  const contextKeys = getBaseKeys({
    status,
    type,
    packaging,
    claimStatus,
    keyPrefix,
  }).map((baseKey) => `${baseKey}.context`);

  const contextValue = rawString({
    langKey: contextKeys.find((langKey) => rawString({ langKey })),
  });

  // Default to email widget if not specified
  if (!contextValue) return 'email';

  // Extract widget type from [[widgetType]] format
  const widgetMatch = contextValue.match(/^\[\[(.+)\]\]$/);
  const widget = widgetMatch?.[1] as InstructionContext | null;

  // Fallback to email if invalid widget type
  return widget ?? 'email';
};

export const hasExplicitWidgetContext = ({
  type,
  packaging,
  widgetType,
  keyPrefix = 'pdx.request_details',
}: {
  type?: string;
  packaging?: PackagingOption | null;
  widgetType: InstructionContext;
  keyPrefix?: string;
}): boolean => {
  const contextKey = `${keyPrefix}.${type}.${packaging}.context`;
  const contextValue =
    rawString({ langKey: contextKey }) ||
    rawString({ langKey: `${contextKey}.1` });

  if (!contextValue) return false;

  // Extract widget type from [[widgetType]] format
  const widgetMatch = contextValue.match(/^\[\[(.+)\]\]$/);
  const widget = widgetMatch?.[1];

  return widget === widgetType;
};

export const resolveWidgetKeys = ({
  status,
  type,
  packaging,
  claimStatus,
  count,
  keyPrefix = 'pdx',
}: {
  status: SubmitStatus;
  type?: string;
  packaging?: PackagingOption | null;
  claimStatus?: ClaimStatus;
  count?: number;
  keyPrefix?: string;
}) => {
  const baseKeys = getBaseKeys({
    status,
    type,
    packaging,
    claimStatus,
    keyPrefix,
  });

  const suffix = count !== undefined ? `.${count}` : '';

  const iconKey = baseKeys
    .map((key) => `${key}.icon${suffix}`)
    .find((key) => rawString({ langKey: key }));

  const descriptionKey = baseKeys
    .map((key) => `${key}.description${suffix}`)
    .find((key) => rawString({ langKey: key }));

  return { iconKey, descriptionKey };
};

export const buildItemsPreviewData = ({
  orderItems,
  requestItems,
  claimId,
}: {
  orderItems?: OrderItem[];
  requestItems?: RequestItemData[];
  claimId?: string;
}) => {
  if (!orderItems || !requestItems || requestItems.length === 0) {
    return undefined;
  }

  const selectedItemIds = requestItems
    .map((item) => item.id)
    .filter((id): id is string => Boolean(id));

  const previewItems = orderItems.filter((item) =>
    selectedItemIds.includes(item.id),
  );

  if (previewItems.length === 0) return undefined;

  const itemCount = previewItems.length;
  const itemCountText =
    rawString({
      langKey:
        itemCount === 1
          ? 'pdx.confirmation.items.header.singular'
          : 'pdx.confirmation.items.header.plural',
      replacementTokens: { item_count: itemCount.toString() },
    }) ?? `${itemCount} item${itemCount === 1 ? '' : 's'}`;

  const viewDetailsText =
    rawString({
      langKey: 'pdx.item_selection.view_details',
    }) ?? 'View details';

  const dialogTitle =
    rawString({
      langKey:
        itemCount === 1
          ? 'pdx.confirmation.items.header.singular'
          : 'pdx.confirmation.items.header.plural',
      replacementTokens: { item_count: itemCount.toString() },
    }) ?? `${itemCount} item${itemCount === 1 ? '' : 's'}`;

  // Filter claims to only include the current claim (avoid showing other claims for same item)
  const itemsWithFilteredClaims = previewItems.map((item) => {
    const claim = claimId
      ? item.claims?.find((c) => c.claimId === claimId)
      : null;
    return {
      ...item,
      claims: claim ? [claim] : null,
    };
  });

  return {
    items: itemsWithFilteredClaims,
    itemCountText,
    viewDetailsText,
    dialogTitle,
  };
};
